import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-button.css?inline';

/**
 * `<pk-button>` — a themed action button with four variants and a loading state.
 * See `docs/components/pk-button.md`.
 *
 * Slots:
 *   - default — button label content
 *
 * Attributes:
 *   - `variant`              — `primary` | `secondary` | `accent` | `danger` (default `primary`)
 *   - `loading`              — convenience boolean; sets `data-submitting="true"`
 *   - `data-submitting`      — `"true"` while submitting; shows `data-submitting-label`,
 *                              sets `aria-busy`, dims opacity, disables pointer events
 *   - `data-submitting-label`— text shown while `data-submitting="true"` (default "Loading…")
 *   - `disabled`             — passes through to the inner `<button>`
 *   - `type`                 — button type, passed to inner `<button>` (default `"button"`)
 */
export class PkButton extends HTMLElement {
    static readonly tagName = 'pk-button';

    // Form-associated custom element. Required so that `<pk-button type="submit">`
    // inside a `<form>` can actually submit the form: the inner `<button type="submit">`
    // lives in pk-button's shadow DOM, where it has no form ancestor to associate with,
    // so a click is a no-op. With formAssociated + attachInternals, `internals.form`
    // resolves to the closest light-DOM form ancestor of the pk-button host, and we
    // can drive `form.requestSubmit()` ourselves on click.
    static readonly formAssociated = true;

    static get observedAttributes(): string[] {
        return ['variant', 'loading', 'data-submitting', 'data-submitting-label', 'disabled', 'type'];
    }

    private readonly _root: ShadowRoot;
    private readonly _internals: ElementInternals;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._internals = this.attachInternals();
    }

    connectedCallback(): void {
        this._root.addEventListener('click', this._handleClick.bind(this));
        this.render();
    }

    attributeChangedCallback(name: string): void {
        // `loading` is a convenience alias for data-submitting="true"
        if (name === 'loading') {
            if (this.hasAttribute('loading')) {
                this.dataset.submitting = 'true';
            } else {
                delete this.dataset.submitting;
            }
        }
        this.render();
    }

    private _handleClick(e: Event): void {
        const isSubmitting = this.dataset.submitting === 'true';
        const isDisabled = this.hasAttribute('disabled');
        if (isSubmitting || isDisabled) {
            e.stopPropagation();
            e.preventDefault();
            return;
        }

        // Submit / reset behaviour for form-associated buttons. Without this,
        // the inner shadow-DOM `<button type="submit">` has no form to submit
        // and the click is silently dropped.
        const btnType = (this.getAttribute('type') ?? 'button').toLowerCase();
        if (btnType === 'submit' || btnType === 'reset') {
            const form = this._internals.form;
            if (form) {
                e.preventDefault();
                if (btnType === 'submit') {
                    form.requestSubmit();
                } else {
                    form.reset();
                }
            }
        }
    }

    private get _isSubmitting(): boolean {
        return this.dataset.submitting === 'true';
    }

    private template(): TemplateResult {
        const variant = this.getAttribute('variant') ?? 'primary';
        const isSubmitting = this._isSubmitting;
        const isDisabled = this.hasAttribute('disabled');
        const btnType = this.getAttribute('type') ?? 'button';
        const submittingLabel = this.dataset.submittingLabel ?? 'Loading…';

        return html`
            <style>${styles}</style>
            <button
                class="btn btn--${variant}"
                type="${btnType}"
                ?disabled="${isDisabled || isSubmitting}"
                aria-busy="${isSubmitting ? 'true' : 'false'}"
                data-submitting="${isSubmitting ? 'true' : 'false'}"
            >
                ${isSubmitting
                    ? html`<span class="label label--submitting">${submittingLabel}</span>`
                    : html`<span class="label"><slot></slot></span>`
                }
            </button>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkButton.tagName)) {
    customElements.define(PkButton.tagName, PkButton);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-button': PkButton;
    }
}
